import { FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@/components/Button";
import { Drawer } from "@/components/Drawer/Drawer";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { IconButton } from "@/components/IconButton/IconButton";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import DeleteTraveller from "@/features/travellers/components/DeleteTraveller";
import {
  useDeleteTravellerMutation,
  useNewTravellerMutation,
  useUpdateTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/features/travellers/utils/ageValidation";
import { getTravelerType } from "@/features/travellers/utils/transformTraveler";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import { type Traveller } from "@/types/types";

import { TravelerTypeLabel } from "./TravelerTypeLabel";
import { TravellerActions } from "./TravellerActions";
import { TravellerCloseConfirmDrawer } from "./TravellerCloseConfirmDrawer";

const TravellerForm = lazy(
  () => import("@/features/travellers/components/TravellerForm"),
);

type Props = {
  travellers: Traveller[];
  travelDate: Date;
  selectedTravellerId: string | null;
  onTravellerSelected: (travellerId: string) => void;
  isSelectionDisabled?: (traveller: Traveller) => boolean;
};

function TravellersList({
  travellers,
  travelDate,
  selectedTravellerId,
  onTravellerSelected,
  isSelectionDisabled,
}: Props) {
  const [travellerForDeletion, setTravellerForDeletion] =
    useState<Traveller | null>(null);
  const [travellerForEdit, setTravellerForEdit] = useState<Traveller | null>(
    null,
  );
  const [newTraveller, setNewTraveller] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const t = useTranslations();
  const { mutateAsync: addTraveller } = useNewTravellerMutation();
  const { mutateAsync: editTraveller } = useUpdateTravellerMutation();
  const { mutateAsync: deleteTraveller } = useDeleteTravellerMutation();
  const [query, setQuery] = useState("");

  const initTravellerDeletion = (traveller: Traveller) => {
    setTravellerForDeletion(traveller);
  };

  const initTravellerEdit = (traveller: Traveller) => {
    setTravellerForEdit(traveller);
  };

  const closeDrawerHandler = () => {
    setTravellerForDeletion(null);
    setTravellerForEdit(null);
    setNewTraveller(false);
  };

  const filteredTravellers = useMemo(
    () =>
      travellers.filter((traveller) =>
        `${traveller.firstName.toLocaleLowerCase()} ${traveller.lastName.toLocaleLowerCase()}`.includes(
          query.toLocaleLowerCase(),
        ),
      ),
    [query, travellers],
  );

  return (
    <>
      <div className="section-card mb-4 text-center">
        <p className="mb-1 text-base font-medium text-text-primary">
          {t("travellers.notOnTheListTitle")}
        </p>
        <p className="text-sm font-normal text-text-secondary">
          {t("travellers.notOnTheListDescription")}
        </p>
        <div className="mt-4 mb-2">
          <Button
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            onClick={() => {
              setNewTraveller(true);
              rudderAnalytics.track(
                CommonTrackingEvents.USER_ENTERED_CREATING_TRAVELER,
              );
            }}
          >
            {t("travellers.addNew")}
          </Button>
        </div>
      </div>
      <div className="section-title">{t("travellers.savedTravellers")}</div>
      <TextField
        placeholder={t("travellers.searchByName")}
        fullWidth
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <MagnifyingGlassIcon
                size={20}
                color="var(--color-text-secondary)"
                weight="bold"
                className="me-2"
              />
            ),
            endAdornment: query ? (
              <IconButton onClick={() => setQuery("")}>
                <XIcon size={20} color="#343330" weight="bold" />
              </IconButton>
            ) : null,
          },
        }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="mt-2">
        {filteredTravellers.length > 0 ? (
          <RadioGroup
            value={selectedTravellerId || ""}
            onChange={(e, travellerId) => {
              onTravellerSelected(travellerId);
            }}
            sx={{
              backgroundColor: "var(--color-white)",
              borderRadius: "var(--radius-2xl)",
            }}
          >
            {filteredTravellers?.map((traveller) => {
              // block the traveller selection if the first travel date (check-in/departure) is less than 6 months from the passport expiry date
              const isExpired = dayjs(travelDate)
                .add(6, "months")
                .isAfter(dayjs(traveller.passportExpiryDate));

              // Calculate expected age group at travel date
              const ageGroupAtTravelDate = getTravellerTypeAtTravelDate(
                traveller.birthDate,
                travelDate,
              );

              // Calculate current age group (from birth date only)
              const ageGroup = getTravelerType(traveller.birthDate);

              const isDisabled = isExpired || isSelectionDisabled?.(traveller);

              return (
                <div
                  key={traveller.id}
                  className="flex justify-between gap-2 border-b border-stroke-neutral-soft px-2 py-4 last:border-b-0"
                >
                  <FormControlLabel
                    value={traveller.id}
                    control={
                      <Radio
                        onChange={(e) => {
                          rudderAnalytics.track(
                            e.target.checked
                              ? CommonTrackingEvents.USER_SELECTED_TRAVELER
                              : CommonTrackingEvents.USER_DESELECTED_TRAVELER,
                            {
                              traveler_type: getTravelerType(
                                traveller.birthDate,
                              ),
                              traveler_type_at_travel_date:
                                getTravellerTypeAtTravelDate(
                                  traveller.birthDate,
                                  travelDate,
                                ),
                            },
                          );
                        }}
                      />
                    }
                    disabled={isDisabled}
                    label={
                      <div className="flex flex-1 flex-col" data-sentry-mask>
                        <span
                          className={clsx(
                            "text-base font-medium",
                            isDisabled
                              ? "text-text-disabled-primary"
                              : "text-text-primary",
                          )}
                        >
                          {traveller.firstName} {traveller.lastName}
                        </span>

                        <span
                          className={clsx(
                            "text-sm",
                            isDisabled
                              ? "text-text-disabled-tertiary"
                              : "text-text-tertiary",
                          )}
                        >
                          <TravelerTypeLabel {...traveller} /> -{" "}
                          {t("travellers.passportId")}:{" "}
                          {traveller.passportNumber}
                        </span>

                        {isExpired ? (
                          <span className="mt-0.5 text-xs text-semantic-error-strong">
                            {t("travellers.passportExpiryHint")}
                          </span>
                        ) : ageGroup !== ageGroupAtTravelDate ? (
                          <span className="mt-0.5 text-xs text-semantic-warning-strong">
                            {t("travellers.ageValidationError", {
                              ageGroupAtTravelDate,
                              age: getTravellerAgeAtTravelDate(
                                traveller.birthDate,
                                travelDate,
                              ),
                            })}
                          </span>
                        ) : null}
                      </div>
                    }
                    labelPlacement="end"
                    sx={{
                      m: 0,
                      display: "flex",
                      gap: "calc(var(--spacing) * 2)",
                    }}
                  />

                  <div className="flex shrink-0 items-center">
                    <TravellerActions
                      traveller={traveller}
                      onDelete={initTravellerDeletion}
                      onEdit={initTravellerEdit}
                    />
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        ) : (
          <div className="flex flex-col items-center gap-1 py-10">
            <div className="font-medium text-black">
              {t("travellers.nothingFound")}
            </div>
            <div className="text-center text-sm text-text-placeholder">
              {query
                ? t("travellers.checkYourInputForTypes")
                : t("travellers.noSavedTravellers")}
            </div>
          </div>
        )}
      </div>
      <Drawer
        open={Boolean(newTraveller || travellerForEdit)}
        disableRestoreFocus
        hasFullHeight
        backgroundColor="var(--color-surface-background)"
      >
        <Suspense
          fallback={
            <PageLoader>
              <AnimatedLoader icon="users" />
            </PageLoader>
          }
        >
          <TravellerForm
            onClose={(reason) => {
              if (reason === "cancelWithPendingChanges") {
                setIsCloseConfirmOpen(true);
                return;
              }

              closeDrawerHandler();
            }}
            traveller={travellerForEdit || null}
            onSubmit={(data) =>
              travellerForEdit
                ? editTraveller({
                    travellerId: travellerForEdit.id,
                    data,
                  })
                : addTraveller(data)
            }
          />
        </Suspense>
      </Drawer>
      <TravellerCloseConfirmDrawer
        open={isCloseConfirmOpen}
        onClose={() => {
          setIsCloseConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCloseConfirmOpen(false);
          closeDrawerHandler();
        }}
      />
      <SwipeableDrawer
        open={Boolean(travellerForDeletion)}
        onClose={() => setTravellerForDeletion(null)}
      >
        {travellerForDeletion ? (
          <DeleteTraveller
            traveller={travellerForDeletion}
            deleteTraveller={() => deleteTraveller(travellerForDeletion.id)}
            closeDrawer={closeDrawerHandler}
          />
        ) : null}
      </SwipeableDrawer>
    </>
  );
}

export default TravellersList;
